/**
 * Pagination.tsx — 列表分页（移植自原型 pagination.jsx）
 *
 * 显示「start — end / 共 total」+ 每页档位 seg + 页码（首尾 + 当前邻近 + 省略号）。
 */

import { useMemo } from 'react';
import { Icons } from '~/lib/icons';

interface PaginationProps {
  total: number;
  page: number;
  pageSize: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  sizeOptions?: number[];
  totalMode?: 'exact' | 'cursor';
  pageItemCount?: number;
  hasMore?: boolean;
}

export function Pagination({
  total,
  page,
  pageSize,
  onChange,
  onPageSizeChange,
  sizeOptions = [18, 30, 50, 100],
  totalMode = 'exact',
  pageItemCount,
  hasMore = false,
}: PaginationProps) {
  const isCursorMode = totalMode === 'cursor';
  const currentPageCount = pageItemCount ?? total;
  const cursorKnownCount = (Math.max(1, page) - 1) * pageSize + currentPageCount;
  const pages = isCursorMode
    ? Math.max(1, page + (hasMore ? 1 : 0))
    : Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(Math.max(1, page), pages);
  const start = isCursorMode
    ? currentPageCount === 0
      ? 0
      : (cur - 1) * pageSize + 1
    : total === 0
      ? 0
      : (cur - 1) * pageSize + 1;
  const end = isCursorMode ? cursorKnownCount : Math.min(cur * pageSize, total);
  const totalLabel = isCursorMode
    ? hasMore
      ? `已载入 ${end.toLocaleString()}+`
      : `共 ${end.toLocaleString()}`
    : `共 ${total.toLocaleString()}`;

  const items = useMemo(() => {
    const list: Array<number | '...'> = [];
    const around = 1;
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || Math.abs(i - cur) <= around) list.push(i);
      else if (list[list.length - 1] !== '...') list.push('...');
    }
    return list;
  }, [pages, cur]);

  return (
    <div
      className="hstack"
      style={{
        padding: '8px 12px',
        borderTop: '1px solid var(--border)',
        gap: 10,
        background: 'var(--bg-subtle)',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
        显示 <span className="mono strong">{start}</span> —{' '}
        <span className="mono strong">{end}</span> /{' '}
        <span className="mono strong">{totalLabel}</span>
      </span>
      {onPageSizeChange && (
        <div className="hstack" style={{ gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>每页</span>
          <div className="seg">
            {sizeOptions.map((s) => (
              <button
                key={s}
                type="button"
                className={s === pageSize ? 'on' : ''}
                onClick={() => onPageSizeChange(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ flex: 1 }} />
      <div className="hstack" style={{ gap: 4 }}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={cur === 1}
          onClick={() => onChange(1)}
          title="首页"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m11 17-5-5 5-5" />
            <path d="m18 17-5-5 5-5" />
          </svg>
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={cur === 1}
          onClick={() => onChange(cur - 1)}
        >
          <Icons.IconChevLeft size={11} />
        </button>
        {items.map((p, i) =>
          p === '...' ? (
            <span key={i} style={{ padding: '0 4px', color: 'var(--fg-faint)', fontSize: 11 }}>
              …
            </span>
          ) : (
            <button
              key={i}
              type="button"
              className="btn btn-sm"
              onClick={() => onChange(p)}
              style={
                p === cur
                  ? {
                      background: 'var(--fg-strong)',
                      color: 'var(--bg)',
                      borderColor: 'var(--fg-strong)',
                    }
                  : {}
              }
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className="btn btn-sm"
          disabled={cur === pages}
          onClick={() => onChange(cur + 1)}
        >
          <Icons.IconChevRight size={11} />
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={isCursorMode || cur === pages}
          onClick={() => onChange(pages)}
          title="末页"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m13 17 5-5-5-5" />
            <path d="m6 17 5-5-5-5" />
          </svg>
        </button>
      </div>
    </div>
  );
}
